import type { FC } from 'react';
import { useLocation, useMatch } from 'react-router';
import type { LoadedAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { useNavigationItems } from 'src/components/layout/app-shell/use-navigation-items';
import type { ActiveSection } from 'src/components/layout/header/types';
import { Link } from 'src/components/navigation/link';

export const isReportingActive = (pathname: string, hash: string) =>
  pathname.startsWith('/reporting') ||
  (pathname.startsWith('/platform') && hash.startsWith('#/reporting'));

type SecondaryNavReportingProps = {
  activeSection: ActiveSection;
  appShellData: LoadedAppShellData;
};

export const SecondaryNavReporting: FC<SecondaryNavReportingProps> = ({
  activeSection,
  appShellData,
}) => {
  const { pathname, hash } = useLocation();
  const navigationItems = useNavigationItems(appShellData);
  const isTrSection = useMatch('/reporting/tr/*');

  if (!activeSection.reporting) return null;

  return (
    <div
      className={`
        flex flex-col flex-wrap items-stretch gap-x-xl gap-y-md
        md:flex-row md:items-center
      `}
      data-testid="secondary-nav"
    >
      {navigationItems.reporting.myTransactions && (
        <Link mode="secondary" to="/reporting/my-transactions">
          My Transactions
        </Link>
      )}

      {/* In new UI products were moved to transport section */}
      {navigationItems.transport.products && (
        <Link
          mode="secondary"
          to="/reporting/products/uploaded"
          isActive={pathname.startsWith('/reporting/products/')}
        >
          Products
        </Link>
      )}

      {navigationItems.reporting.standardReports && (
        <Link mode="secondary" to="/reporting/standard">
          Standard Reports
        </Link>
      )}

      {navigationItems.reporting.auctions && (
        <Link mode="secondary" to="/reporting/auctions">
          Auctions
        </Link>
      )}

      {navigationItems.reporting.secondaryTrading && (
        <Link
          mode="secondary"
          to="/reporting/secondary"
          isActive={
            pathname.startsWith('/reporting/secondary') ||
            (pathname.startsWith('/platform') &&
              hash.startsWith('#/reporting/secondary'))
          }
        >
          Secondary Trading
        </Link>
      )}

      {navigationItems.reporting.lng && (
        <Link mode="secondary" to="/reporting/lng">
          LNG
        </Link>
      )}

      {navigationItems.reporting.shipperTransactions && (
        <Link mode="secondary" to="/reporting/shipper-transactions">
          Shipper Transactions
        </Link>
      )}

      {navigationItems.reporting.monthlyStatements && (
        <Link mode="secondary" to="/reporting/monthly-statements">
          Monthly Statements
        </Link>
      )}

      {navigationItems.reporting.balancingGroupAllocations && (
        <Link mode="secondary" to="/reporting/balancing-group-allocations">
          Balancing Group Allocations
        </Link>
      )}

      {(navigationItems.reporting.tr || isTrSection) && (
        <Link mode="secondary" to="/reporting/tr">
          Transparency Reporting
        </Link>
      )}

      {navigationItems.reporting.remitReports && (
        <Link mode="secondary" to="/reporting/remit-reports">
          REMIT Reports
        </Link>
      )}

      {navigationItems.reporting.surrenders && (
        <Link
          mode="secondary"
          to="/reporting/surrenders"
          isActive={
            pathname.startsWith('/reporting/surrenders') ||
            (pathname.startsWith('/platform') &&
              hash.startsWith('#/reporting/surrender/details'))
          }
        >
          Surrenders
        </Link>
      )}
    </div>
  );
};
